import { HUImageDtoSchema } from "@modules/dtos/imageDto"
import { Modality } from "@modules/profile/publicProfile/types/publicProfileTypes"
import { ProgramBundleItemDtoSchema } from "@modules/programBundle/api/programBundleDto"
import { UserProgramItemDtoSchema } from "@modules/programs/gateway/userProgramItemDto"
import { ScoreType } from "@modules/scores/scoreTypes"
import { z } from "zod"

export const GenderCategory = {
  MALE: "MALE",
  FEMALE: "FEMALE",
} as const

export type GenderCategory = (typeof GenderCategory)[keyof typeof GenderCategory]

export const MovementDtoSchema = z
  .object({
    id: z.string(),
    movementName: z.string(),
    description: z.string(),
    modality: z.enum(Modality),
    scoreType: z.enum(ScoreType),
    videoUrl: z.string().nullable(),
  })
  .meta({ id: "MovementDtoSchema" })

export const PersonalRecordDtoSchema = z
  .object({
    id: z.string(),
    userId: z.string(),
    movementId: z.string(),
    value: z.number(),
    repetitionMax: z.number().nullable(),
    date: z.iso.date(),
    comment: z.string().nullable(),
  })
  .meta({ id: "PersonalRecordDtoSchema" })

export const BadgeDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    code: z.string(),
    date: z.iso.date(),
    obtainable: z.boolean(),
    image: HUImageDtoSchema,
  })
  .meta({ id: "BadgeDtoSchema" })

export const UserBadgeDtoSchema = z
  .object({
    userBadgeId: z.string(),
    badge: BadgeDtoSchema,
    additionalInformation: z.string().optional(),
    date: z.iso.datetime(),
  })
  .meta({ id: "UserBadgeDtoSchema" })

export const FavoritePersonalRecordDtoSchema = z
  .object({
    movement: MovementDtoSchema,
    personalRecord: PersonalRecordDtoSchema.nullable(),
    favorite: z.boolean().nullable(),
  })
  .meta({ id: "FavoritePersonalRecordDtoSchema" })

export const PublicProfileDtoSchema = z
  .object({
    userId: z.string(),
    username: z.string(),
    firstname: z.string().nullable(),
    lastname: z.string().nullable(),
    biography: z.string().nullable(),
    avatar: HUImageDtoSchema.nullable(),
    birthdate: z.string().nullable(),
    crossfitStartDate: z.string().nullable(), // TODO : check if this is a date timestamp
    genderCategory: z.enum(GenderCategory).nullable(),
    job: z.string().nullable(),
    nationality: z.string().nullable(),
    height: z.number().nullable(),
    weight: z.number().nullable(),
    favoritesPersonalRecords: z.array(z.lazy(() => FavoritePersonalRecordDtoSchema)),
    ownedPrograms: z.array(UserProgramItemDtoSchema),
    followedPrograms: z.array(UserProgramItemDtoSchema),
    followedProgramBundle: z.array(ProgramBundleItemDtoSchema),
    sharedCoachPrograms: z.array(UserProgramItemDtoSchema),
    badges: z.array(UserBadgeDtoSchema),
  })
  .meta({ id: "PublicProfileDtoSchema" })

export type FavoritePersonalRecordDto = z.infer<typeof FavoritePersonalRecordDtoSchema>
export type UserBadgeDto = z.infer<typeof UserBadgeDtoSchema>
export type MovementDto = z.infer<typeof MovementDtoSchema>
export type PersonalRecordDto = z.infer<typeof PersonalRecordDtoSchema>
export type BadgeDto = z.infer<typeof BadgeDtoSchema>
export type PublicProfileDto = z.infer<typeof PublicProfileDtoSchema>
